// frontend/src/utils/units.ts
//
// Canonical unit taxonomy — the frontend mirror of
// backend/src/utils/unitConversion.js. Keep the two in sync: canonical
// spellings are 'L' (upper) and 'ml' (lower); families (mass, volume) are the
// only convertible dimensions. Count units (pair/box/unit) and pack units
// (bag/drum/…) are NOT convertible here.
//
// NOTE: the legacy widget math (chemicalStockUsageMath.ts /
// fertilizerStockUsageMath.ts) uses 'ml' for display down-conversion
// (canonicalized in slice 5).

export type UnitFamily = 'mass' | 'volume';

export const UNIT_FAMILIES: Record<UnitFamily, Record<string, number>> = {
  volume: { L: 1000, ml: 1 },
  mass:   { t: 1000000, kg: 1000, g: 1 },
};

export const UNITS_BY_FAMILY: Record<UnitFamily, string[]> = {
  mass:   ['t', 'kg', 'g'],
  volume: ['L', 'ml'],
};

export const MEASURE_UNITS: string[] = [
  ...UNITS_BY_FAMILY.mass,
  ...UNITS_BY_FAMILY.volume,
];

export const ALLOWED_UNITS_BY_TYPE: Record<string, string[]> = {
  chemical:   ['t', 'kg', 'g', 'L', 'ml'],
  fertilizer: ['t', 'kg', 'g', 'L', 'ml'],
  diesel:     ['L', 'ml'],
  ppe:        ['pair', 'box', 'unit'],
};

export function familyOf(unit: string | null | undefined): UnitFamily | null {
  if (unit == null) return null;
  for (const family of Object.keys(UNIT_FAMILIES) as UnitFamily[]) {
    if (Object.prototype.hasOwnProperty.call(UNIT_FAMILIES[family], unit)) {
      return family;
    }
  }
  return null;
}

export function areUnitsConvertible(a: string, b: string): boolean {
  if (a === b) return true;
  const fa = familyOf(a);
  return fa !== null && fa === familyOf(b);
}

export function convertQuantity(
  qty: number,
  fromUnit: string,
  toUnit: string,
): number | null {
  if (fromUnit === toUnit) return qty;
  const family = familyOf(fromUnit);
  if (family === null || family !== familyOf(toUnit)) return null;
  const units = UNIT_FAMILIES[family];
  return (qty * units[fromUnit]) / units[toUnit];
}

const CANON_BY_LOWER: Record<string, string> = MEASURE_UNITS.reduce(
  (acc, u) => {
    acc[u.toLowerCase()] = u;
    return acc;
  },
  {} as Record<string, string>,
);

export function canonicalUnit(
  raw: string | null | undefined,
): { unit: string; family: UnitFamily } | null {
  if (raw == null) return null;
  const hit = CANON_BY_LOWER[String(raw).trim().toLowerCase()];
  if (hit === undefined) return null;
  const family = familyOf(hit);
  return family === null ? null : { unit: hit, family };
}

export function allowedUnitsForType(consumableType: string): string[] {
  return ALLOWED_UNITS_BY_TYPE[consumableType] ?? [];
}
